import { useNavigate } from "@tanstack/react-router";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { usePagination } from "@/api/pagination";
import { LogoStack } from "@/components/admin/logo-stack";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table, TableCard } from "@/components/application/table/table";
import { useCache } from "@/hooks/use-cache";
import { useSort } from "@/hooks/use-sort";

type AdminWorkspacesTableProps = {
  search: string;
  /** Pass both to keep the current page outside the table (e.g. in the URL). */
  page?: number;
  onPageChange?: (page: number) => void;
};

export const AdminWorkspacesTable = ({ search, page, onPageChange }: AdminWorkspacesTableProps) => {
  const navigate = useNavigate();
  const pagination = usePagination(undefined, page !== undefined && onPageChange ? { page, onPageChange } : undefined);
  const sort = useSort<"name" | "createdAt">("name", "asc");
  const { data } = useAdminWorkspaces(pagination.pagination, sort.sort, { text: search });
  const total = useCache(data?.total);

  return (
    <TableCard.Root className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <Table aria-label="Company Workspaces" sortDescriptor={sort.descriptor} onSortChange={sort.setFromDescriptor}>
          <Table.Header>
            <Table.Head id="name" label="Name" isRowHeader allowsSorting className="w-[35%]" />
            <Table.Head id="customerType" label="Customer type" className="w-[15%]" />
            <Table.Head id="brands" label="Merchants" className="w-[20%]" />
            <Table.Head id="users" label="Users" className="w-[15%]" />
            <Table.Head id="createdAt" label="Created" allowsSorting className="w-[15%]" />
          </Table.Header>

          <Table.Body items={data?.workspaces ?? []}>
            {(item) => (
              <Table.Row id={item.id}>
                <Table.Cell className="whitespace-nowrap p-0">
                  <button
                    type="button"
                    onClick={() => navigate({ to: `/admin/customers/${item.id}` })}
                    className="w-full h-full flex items-center gap-3 px-4 py-3"
                  >
                    <MerchantLogo name={item.name} logoUrl={item.logoUrl} size="sm" />
                    <p className="text-sm font-medium text-primary">{item.name}</p>
                  </button>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <span className="text-sm text-primary">
                    {item.customerType === "brand" ? "Merchant" : item.customerType === "agency" ? "Agency" : "—"}
                  </span>
                </Table.Cell>
                <Table.Cell className="min-w-[160px]">
                  {item.merchants.length > 0 ? (
                    <LogoStack items={item.merchants} />
                  ) : (
                    <span className="text-sm text-tertiary">No merchants yet</span>
                  )}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <span className="text-sm text-primary">{item.users.length}</span>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {new Date(item.createdAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  })}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>

      <PaginationPageMinimalCenter
        page={pagination.page}
        total={Math.ceil((total ?? 0) / pagination.pagination.limit)}
        onPageChange={pagination.setPage}
        className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
      />
    </TableCard.Root>
  );
};
